import React, {useEffect, useRef} from "react";
import { styled } from '@mui/material/styles';
import {pocPrimary, MoreOptsMenu, pageContentGap} from "../theme";
import {
    ClickAwayListener,
    Fade,
    Grid,
    IconButton,
    Paper,
    Popper,
    TableCell,
    TableRow,
    Toolbar,
    Tooltip,
} from "@mui/material";

import CatalogCheckbox from "./EnhancedCheckbox";
import {MoreVert, PriorityHigh} from "@mui/icons-material";
import CatalogFilters, {addFiltersToTable} from "./CatalogFilters/catalog_filters";
import CatalogManagerTable from "./CatalogManagerTable";
import { SavedSearchManager } from "./saved_search";
import { getTableConfig } from './saved_search/utils/getConfig';
import { applySavedSearchConfig } from './saved_search/utils/applyConfig';

// CatalogFilters has a strict props interface this loose caller doesn't satisfy; preserve prior untyped behavior.
const CatalogFiltersAny: any = CatalogFilters;


// Filters an array of rows by a lowercase search term. Object rows are matched
// by stringifying each field value; otherwise rows are matched by their
// searchable columns. Exported for unit testing.
// NOTE: JSON.stringify(undefined) returns undefined (not a string), so the
// stringified value is coalesced to "" before calling toLowerCase() — without
// this, a row carrying an undefined field value threw "Cannot read properties
// of undefined (reading 'toLowerCase')" and crashed the search.
export const filterDataBySearchTerm = (data: any[], term: string | null | undefined, columns: any[]): any[] => {
    if (term == null || term === "") return data;

    const split = term.toLowerCase().split(" ");
    const fields = columns.filter(column => column.searchable).map(c => c.id);

    // If data is an array of objects, filter by checking if any field contains the term.
    if (data.length > 0 && typeof data[0] === "object") {
        return data.filter(r =>
            Object.entries(r).filter(([key, value]) => {
                let stringified = "";

                try {
                    stringified = JSON.stringify(value) ?? "";
                } catch (e) {
                    return false;
                }

                return split.every(s => stringified.toLowerCase().indexOf(s) > -1);
            }).length > 0
        );
    }

    return data.filter(r =>
        split.every(s =>
            fields.filter(f => r[f] && r[f].toLowerCase().indexOf(s) > -1).length > 0
        )
    );
};


const BulkUpdateStatus = styled(Grid)({
    flexDirection: "column",
    width: "306px",
    padding: "12px 8px",
    margin: "16px",
    background: "#FFFFFF",
    border: "1px solid #DCDCDC",
    borderRadius: "6px",
    fontSize: "14px",
    gap: pageContentGap
});

export { BulkUpdateStatus };

const TooltipContent = styled('div')(({ theme }) => ({
    padding: '4px 8px',
    fontSize: '12px',
    fontFamily: theme.typography.fontFamily,
    color: 'rgba(0, 0, 0, 0.87)',
    backgroundColor: '#ffffff',
    borderRadius: '2px'
}));

const StyledTooltip = ({ children, ...props }: any) => (
    <Tooltip
        {...props}
        slotProps={{
            tooltip: {
                sx: {
                    backgroundColor: '#ffffff',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
                    margin: 0,
                }
            },
            arrow: {
                sx: {
                    color: '#ffffff',
                    '&::before': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
                    }
                }
            },
            ...props.slotProps,
        }}
    >
        {children}
    </Tooltip>
);

const StyledTableCell = styled(TableCell)({
    padding: '8px',
    verticalAlign: 'middle',
});

export const ContentWithTooltip = ({cellContent, column, row, colIndex, rowIndex, classes, addFilter}: any) => {
    const contentRef = React.useRef(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    const checkOverflow = React.useCallback(() => {
        if (contentRef.current) {
            setIsOverflowing(
                contentRef.current.scrollWidth > contentRef.current.clientWidth
            );
        }
    }, []);

    React.useEffect(() => {
        checkOverflow();

        const handleResize = () => {
            checkOverflow();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [checkOverflow, cellContent]);

    const content = (
        <div
            ref={contentRef}
            style={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: column.hoverTemplate && (isOverflowing || column.alwaysShowTooltip) ? 'pointer' : 'inherit'
            }}
        >
            {cellContent}
        </div>
    );

    return column.hoverTemplate && (isOverflowing || column.alwaysShowTooltip) ? (
        <StyledTooltip
            title={
                <TooltipContent>
                    {column.hoverTemplate(row, column, colIndex, rowIndex, addFilter)}
                </TooltipContent>
            }
            placement="bottom-start"
            arrow
            interactive={column.hoverTemplateInteractive}
            enterDelay={200}
            leaveDelay={column.hoverTemplateLeaveDelay || 0}
            slotProps={{
                popper: {
                    modifiers: {
                        offset: {
                            enabled: true,
                            offset: '0, 2',
                        },
                    }
                }
            }}
        >
            {content}
        </StyledTooltip>
    ) : content;
};

function CatalogManagerResultsMoreOpts(props) {
    const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);

    return (
        <TableCell sx={(theme) => ({
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "250px",
            padding: theme.spacing(1),
            width: 0,
            p: "4px 0"
        })}
                   onClick={(e) => {
                       e.stopPropagation();
                       e.preventDefault();
                   }}
        >
            <IconButton
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(!open);
                    setAnchorEl(e.currentTarget);
                }}
                style={{borderRadius: 100}}
                size="large">
                <MoreVert/>
            </IconButton>
            <Popper open={open} anchorEl={anchorEl} transition placement={"left-start"} style={{zIndex: 1300}}>
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={() => {
                        setOpen(false);
                        setAnchorEl(null)
                    }} mouseEvent="onMouseDown">
                        <Fade {...TransitionProps} timeout={350}>
                            <MoreOptsMenu>
                                {props.template}
                            </MoreOptsMenu>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </TableCell>
    );
}

// Helper function to extract table configuration for saved searches

interface CatalogManagerResultsProps {
    table: any;
    config: any;
    condensed?: boolean;
    noToolBar?: boolean;
    tableHeader?: React.ReactNode;
    showSavedSearch?: boolean;
    savedSearchTableType?: any;
    customFields?: any;
    setConfig?: (config: any) => void;
    noFilters?: boolean;
    bufferExtensions?: React.ReactNode;
    rowOnClick?: (row: any) => void;
    isSelectable?: boolean;
    moreTableOptions?: any;
    generateRows?: () => any;
    filterUiFunc?: (data: any[]) => any[];
    [key: string]: any;
}

function CatalogManagerResults(props: CatalogManagerResultsProps) {
    let table = props.table;


    const configRef = useRef<any>(undefined);
    configRef.current = props.config;

    // Format CatalogManagerTable Data Into Rows //
    const generateRows = () => {
        let dataToRender = [...table.data];
        if (props.filterUiFunc) {
            dataToRender = props.filterUiFunc(dataToRender);
        }

        return dataToRender.map((row, rowIndex) => {
            let isSelectable = props.isSelectable ?? false;
            let rowId = row.id;
            if (configRef.current) {
                if (typeof configRef.current.keyField === 'function') {
                    rowId = configRef.current.keyField(row);
                } else {
                    rowId = row[configRef.current.keyField];
                }
            }

            return (
                <TableRow key={rowIndex}
                          sx={(theme) => ({
                              '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
                              '&:nth-of-type(even)': { backgroundColor: "#F9F9F9" },
                              height: "70px",
                              cursor: props.rowOnClick ? "pointer" : "auto"
                          })}
                          onClick={() => props.rowOnClick ? props.rowOnClick(row) : null}
                >
                    {isSelectable && (
                        <TableCell padding="checkbox" style={{width: '3em'}}>
                            <CatalogCheckbox checked={props.table.extensions.isSelected(rowId)}
                                             onChange={(e) => props.table.extensions.handleClick(e, rowId)}
                                             onClick={(e) => e.stopPropagation()}
                            />
                        </TableCell>
                    )}
                    {props.config.columns.map((column, colIndex) => {
                        if (props.table.extensions.isColumnHidden(column)) return null;
                        if (column.hidden && !column.toggleable) return null;

                        let cellContent = column.template
                            ? column.template(row, column, colIndex, rowIndex, (filter) => addFiltersToTable([filter], props.table))
                            : row[column.id];

                        let content = (
                            <StyledTableCell
                                key={`${column.id}-${rowIndex}`}
                                style={{
                                    width: column.width,
                                    maxWidth: column.width,
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    padding:
                                    (!isSelectable)
                                        ? (props.nonCondensedRows ? "4px 0px 4px 24px" : undefined)
                                        : "0 6px 0 0",
                                    ...column.cellStyle,
                                }}
                            >
                                <ContentWithTooltip
                                    cellContent={cellContent}
                                    column={column}
                                    row={row}
                                    colIndex={colIndex}
                                    rowIndex={rowIndex}
                                    addFilter={(filter) => addFiltersToTable([filter], props.table)}
                                />
                            </StyledTableCell>
                        );

                        return <React.Fragment key={`${rowIndex}-${colIndex}`}>{content}</React.Fragment>
                    })}
                    {props.moreTableOptions && <CatalogManagerResultsMoreOpts template={props.moreTableOptions(row)}/>}
                    <TableCell sx={(theme) => ({
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "250px",
                        padding: theme.spacing(1),
                        width: 0,
                        p: "4px 0"
                    })}
                               onClick={(e) => {
                                   e.stopPropagation();
                                   e.preventDefault();
                               }}
                    >
                        <Grid container style={{flexDirection: "column", margin: "0"}}>
                        </Grid>
                    </TableCell>
                    <TableCell sx={(theme) => ({
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "250px",
                        padding: theme.spacing(1),
                        width: 0,
                        p: "4px 20px 4px 0"
                    })}>
                        {row.errorMessage && (
                            <Grid container justifyContent={"center"} alignItems={"center"}>
                                <Tooltip title={"This product has an error, please open to view."}>
                                    <PriorityHigh style={{color: "#f44336"}}/>
                                </Tooltip>
                            </Grid>
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    };

    //region Lifecycle
    useEffect(() => {
        // Establish search term filtering if custom getData function //
        if (props.config.getData) {
            const getData = (callback, columns) => {
                /* getData is defined in configuration, however, in the situation we want to apply outside parameters/redefine
                   this function we create a callback. In this case, on component creation we use a useEffect to update the
                   definition to this function while passing in the previous getData function. Then we use the previous
                   getData function to get any data then perform operations on it with outside information. In this case, term. */
                return async () => {
                    const data = await callback.apply(null, [configRef.current.term]);
                    return filterDataBySearchTerm(data, configRef.current.term, columns);
                };
            };

            configRef.current.getData = getData(props.config.getData, props.config.columns);
        }

        // eslint-disable-next-line
    }, [props.config.getData]);
    //endregion

    if (props.condensed) {
        return (
            <Paper style={{width: "100%"}}>
                {props.noToolBar ? (
                    <Grid>
                        {props.tableHeader}
                    </Grid>
                ) : (
                    <Toolbar className='lbtoolbar'
                             style={{background: pocPrimary, borderRadius: "4px 4px 0 0"}}
                    >
                        {props.tableHeader}
                    </Toolbar>
                )}

                {/*region Saved Search*/}
                {props.showSavedSearch && (
                    <Grid container style={{padding: '10px 12px'}}>
                        <SavedSearchManager
                            tableType={props.savedSearchTableType}
                            tableState={(() => {
                                const baseConfig = getTableConfig(props.table, props.filterShowExceptions, props.config);
                                const configWithCustom = {
                                    ...baseConfig,
                                    customFields: props.customFields || props.config?.customFields || {}
                                };
                                return configWithCustom;
                            })()}
                            initialFilters={props.config.filter}
                            variant={props.condensed ? 'condensed' : 'standard'}
                            onConfigApplied={(config) => {
                                applySavedSearchConfig(config, {
                                    table: props.table,
                                    columns: props.config.columns,
                                    setConfig: props.setConfig,
                                    customFields: config.customFields
                                });
                            }}
                        />
                    </Grid>
                )}
                {/*endregion*/}
                
                {/*region Catalog Filters*/}
                {props.noFilters ? null : (
                    <Grid container style={props.inLineFilters ? {marginBottom: "-60px"} : {}}>
                        <CatalogFiltersAny
                            filterShowExceptions={props.filterShowExceptions}
                            renderFiltersAfter={props.renderButtonFiltersAfter}
                            Filters={props.buttonFilters}
                            disableDetailedFilters={props.disableDetailedFilters}
                            disableColumnSelector={props.disableColumnSelector}
                            disableSearch={props.disableSearch}
                            disableTermInUrl={props.disableTermInUrl}
                            table={props.table}
                            condensed={props.condensed}
                            config={props.config} setConfig={props.setConfig}
                            history={props.history} location={props.location}
                            filterNullExceptions={props.filterNullExceptions}
                        />
                    </Grid>
                )}
                {/*endregion*/}

                {/*region Table */}
                <Grid container style={{overflow: "auto", flexDirection: "column", justifyContent: "flex-start"}}>
                    <CatalogManagerTable
                        forceRefresh={props.forceRefresh}
                        isRetailer={props.isRetailer}
                        hasInlineFilters={props.inLineFilters}
                        hideTopPagination={props.hideTopPagination}
                        extensions={props.extensions}
                        table={table}
                        config={props.config}
                        bulkActions={props.bulkActions}
                        generateRows={props.generateRows || generateRows}
                        isSelectable={props.isSelectable ?? false}
                        tableMessage={props.tableMessage}
                        moreTableOptions={props.moreTableOptions}
                        disableLoadingPlaceholder={props.disableLoadingPlaceholder}
                    />
                </Grid>
                {/*endregion*/}
            </Paper>
        )
    }

    return (
        <Grid container style={{ width: '100%', ...props.resultsContainerStyles }}>
            {/*region Saved Search*/}
            {props.showSavedSearch && (
                <Paper style={{width: "100%", padding: '10px 12px'}}>
                    <SavedSearchManager
                        tableType={props.savedSearchTableType}
                        tableState={(() => {
                            const baseConfig = getTableConfig(props.table, props.filterShowExceptions, props.config);
                            const configWithCustom = {
                                ...baseConfig,
                                customFields: props.customFields || props.config?.customFields || {}
                            };
                            return configWithCustom;
                        })()}
                        initialFilters={props.config.filter}
                        variant={props.condensed ? 'condensed' : 'standard'}
                        onConfigApplied={(config) => {
                            applySavedSearchConfig(config, {
                                table: props.table,
                                columns: props.config.columns,
                                setConfig: props.setConfig,
                                customFields: config.customFields
                            });
                        }}
                    />
                </Paper>
            )}
            {/*endregion*/}
            
            {/*region Catalog Filters*/}
            {props.noFilters ? null : (
                <Paper style={{width: "100%"}}>
                    <Grid container>
                        <CatalogFiltersAny
                            filterShowExceptions={props.filterShowExceptions}
                            renderFiltersAfter={props.renderButtonFiltersAfter}
                            Filters={props.buttonFilters}
                            disableDetailedFilters={props.disableDetailedFilters}
                            disableColumnSelector={props.disableColumnSelector}
                            disableSearch={props.disableSearch}
                            disableTermInUrl={props.disableTermInUrl}
                            table={props.table}
                            config={props.config} setConfig={props.setConfig}
                            history={props.history} location={props.location}
                            filterNullExceptions={props.filterNullExceptions}
                        />
                    </Grid>
                </Paper>
            )}
            {/*endregion*/}

            {props.bufferExtensions && (
                <Paper style={{width: "100%"}}>
                    <Grid container>
                        {props.bufferExtensions}
                    </Grid>
                </Paper>
            )}

            {/*region Table */}
            <Paper style={{width: "100%"}}>
                <Grid container style={{overflow: "auto", flexDirection: "column", justifyContent: "flex-start"}}>
                    <CatalogManagerTable
                        forceRefresh={props.forceRefresh}
                        extensions={props.extensions}
                        isRetailer={props.isRetailer}
                        hideTopPagination={props.hideTopPagination}
                        table={table}
                        config={props.config}
                        bulkActions={props.bulkActions}
                        generateRows={props.generateRows || generateRows}
                        isSelectable={props.isSelectable ?? false}
                        tableMessage={props.tableMessage}
                        moreTableOptions={props.moreTableOptions}
                        disableLoadingPlaceholder={props.disableLoadingPlaceholder}
                    />
                </Grid>
            </Paper>
            {/*endregion*/}
        </Grid>
    )
}

export default CatalogManagerResults;
